// Campaigns — rich form with relational pickers (no JSON).
import { useEffect, useState } from "react";
import { Plus, Edit2, Trash2, Loader2, Rocket } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { useCurrentModulePerms } from "@/contexts/PermissionsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import RelationalPicker from "@/admin/components/RelationalPicker";
import CallingWindowEditor from "@/admin/components/CallingWindowEditor";

const MODES = [
  { v: "preview",       label: "Preview (agent confirms each lead)" },
  { v: "progressive",   label: "Progressive (auto-dial as agent free)" },
  { v: "power",         label: "Power dial (1:N Auto dial)" },
  { v: "predictive",    label: "Predictive (Erlang-C oversample)" },
  { v: "blended",       label: "Blended (inbound + outbound)" },
];

export default function CampaignsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [crmScreens, setCrmScreens] = useState([]);
  const [leadLists, setLeadLists] = useState([]);
  const { canWrite, canDelete } = useCurrentModulePerms();

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/admin/resources/campaigns");
      // Voice-broadcast campaigns are managed entirely on the Voice Broadcast
      // page — hide them here so they don't appear in both places.
      setRows((r.data || []).filter((c) => (c.data?.mode || "") !== "voice_broadcast"));
    }
    catch (e) { toast.error(formatErr(e)); } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/admin/crm-screens").then((r) => setCrmScreens(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { api.get("/admin/resources/lead_lists").then((r) => setLeadLists(r.data || [])).catch(() => {}); }, []);

  const mappedLists = (campId) => leadLists.filter((l) => l.data?.campaign_id === campId);

  function defaults() {
    return {
      name: "", description: "", is_active: true,
      mode: "progressive",
      // Slice 3.2 — per-campaign channel-mode default. `auto` follows the
      // dial mode (predictive/progressive/blended → off-hook, others → on-hook).
      // Admin can override to lock a specific behavior for this campaign.
      channel_mode_default: "auto",
      lead_list_id: "",
      trunk_id: "",
      caller_id_pool_id: "",
      skills_required: [],
      music_on_hold_id: "",
      working_hours_id: "",
      // Enforced operator calling window (start/stop hours). When enabled, NO
      // outbound/preview dials go out outside this window; auto-resumes next day.
      calling_window: {},
      compliance_rule_id: "",
      dnc_list_ids: [],
      allowed_dispositions: [],
      allowed_pause_codes: [],
      auto_wrapup_enabled: false,
      auto_wrapup_seconds: 30,
      auto_wrapup_code_id: "",
      amd_enabled: true,
      // Blended
      linked_queue_id: "",   // legacy singular field kept for back-compat
      linked_queues: [],     // Slice 3.2b — multi-queue mapping
      blended_priority: "inbound_first",
      // Retry
      retry_max_attempts: 4, retry_gap_minutes: 60, retry_recycle_after_days: 7,
      // Predictive
      abandon_target_pct: 3.0,
      // Power mode dial level (1:N oversample). Range 1.0 - 4.0.
      // Only meaningful when mode === "power".
      dial_level: 1.0,
      // Slice 3.5 — bypass `in_compliance_window` for staging/smoke tests.
      // Default off (compliance is a HARD legal requirement in production).
      skip_compliance: false,
      // Slice 3.5b — Routing strategy when multiple off-hook agents are
      // active on the same campaign. Default keeps existing behavior.
      routing_strategy: "round_robin",
      // Slice 3.5m — Vicidial-style staging: dial UNASSIGNED customers paced
      // by dial level / Erlang-C, then allocate an available agent the moment
      // the customer answers. Default ON for predictive & power modes.
      stage_dialing: true,
      customer_max_wait_seconds: 3,
      // Slice 16 — campaign-level call recording (Asterisk MixMonitor).
      // When enabled, BOTH inbound (via linked queues) and outbound
      // predictive/power calls of this campaign are recorded on the server.
      record_calls: false,
      recording_rule_id: "",
      recording_format: "wav",
      // CRM screen (agent lead-popup field layout) for this campaign.
      crm_screen_id: "",
    };
  }
  function startNew() { setForm(defaults()); setEditing("new"); }
  function startEdit(r) {
    const d = r.data || {};
    setForm({
      ...defaults(),
      name: r.name, description: r.description||"", is_active: !!r.is_active,
      ...Object.fromEntries(Object.entries(d).filter(([k]) => k in defaults())),
    });
    setEditing(r);
  }
  async function save() {
    if (!form.name?.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      const { name, description, is_active, ...rest } = form;
      const payload = { name: name.trim(), description: description||"", is_active: !!is_active, data: rest };
      if (editing === "new") await api.post("/admin/resources/campaigns", payload);
      else await api.patch(`/admin/resources/campaigns/${editing.id}`, payload);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(formatErr(e)); } finally { setSaving(false); }
  }
  async function remove(r) {
    if (!confirm(`Delete "${r.name}"?`)) return;
    try { await api.delete(`/admin/resources/campaigns/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  }

  return (
    <div className="px-4 sm:px-6 py-6 max-w-screen-2xl">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight" data-testid="camp-title">Campaigns</h1>
          <p className="text-sm text-muted-foreground mt-1">Outbound or inbound-linked (blended) campaigns. All targets/lists/rules are pickers — no free text.</p>
        </div>
        {canWrite && <Button data-testid="add-camp-btn" onClick={startNew} className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5"><Plus className="w-4 h-4"/> New campaign</Button>}
      </div>
      <Card className="overflow-hidden">
        {loading ? <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div> : (
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-muted-foreground"><tr className="text-left">
              <th className="px-4 py-2">Name</th><th className="px-4 py-2">Mode</th><th className="px-4 py-2">Lead lists</th><th className="px-4 py-2">Active</th><th className="px-4 py-2 w-24">Actions</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border hover:bg-muted/30" data-testid={`row-camp-${r.id}`}>
                  <td className="px-4 py-2.5"><div className="flex items-center gap-2"><Rocket className="w-4 h-4 text-primary"/><span className="font-medium">{r.name}</span></div>{r.description && <div className="text-xs text-muted-foreground ml-6">{r.description}</div>}</td>
                  <td className="px-4 py-2.5"><Badge variant="outline" className="text-[10px] uppercase">{r.data?.mode||"—"}</Badge></td>
                  <td className="px-4 py-2.5 text-xs">{(() => { const n = mappedLists(r.id).length; return n ? <Badge variant="secondary" className="text-[10px]">{n} list{n>1?"s":""}</Badge> : <span className="text-amber-600">None</span>; })()}</td>
                  <td className="px-4 py-2.5">{r.is_active ? <Badge variant="outline" className="text-[10px]">On</Badge> : <Badge variant="destructive" className="text-[10px]">Off</Badge>}</td>
                  <td className="px-4 py-2.5"><div className="flex gap-1">
                    {canWrite && <Button data-testid={`edit-camp-${r.id}`} variant="ghost" size="icon" className="h-8 w-8" onClick={()=>startEdit(r)}><Edit2 className="w-4 h-4"/></Button>}
                    {canDelete && <Button data-testid={`del-camp-${r.id}`} variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={()=>remove(r)}><Trash2 className="w-4 h-4"/></Button>}
                  </div></td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-sm text-muted-foreground">No campaigns yet.</td></tr>}
            </tbody>
          </table>
        )}
      </Card>

      <Dialog open={!!editing} onOpenChange={(v)=>!v&&setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing==="new"?"New campaign":`Edit ${editing?.name}`}</DialogTitle><DialogDescription>Configure dial mode, lists, skills, and policies.</DialogDescription></DialogHeader>
          <Tabs defaultValue="basics" className="py-2">
            <TabsList className="flex-wrap">
              <TabsTrigger value="basics" data-testid="camp-tab-basics">Basics</TabsTrigger>
              <TabsTrigger value="routing" data-testid="camp-tab-routing">Routing</TabsTrigger>
              <TabsTrigger value="schedule" data-testid="camp-tab-schedule">Schedule</TabsTrigger>
              <TabsTrigger value="compliance" data-testid="camp-tab-compliance">Compliance</TabsTrigger>
              <TabsTrigger value="agent" data-testid="camp-tab-agent">Agent</TabsTrigger>
              <TabsTrigger value="blended" data-testid="camp-tab-blended">Blended</TabsTrigger>
            </TabsList>

            <TabsContent value="basics" className="space-y-3 mt-3">
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2"><Label>Name</Label><Input data-testid="camp-name" value={form.name||""} onChange={(e)=>setForm({...form, name:e.target.value})} /></div>
                <div className="flex items-center gap-2 mt-7"><Switch data-testid="camp-active" checked={!!form.is_active} onCheckedChange={(v)=>setForm({...form, is_active:!!v})} /><Label>Active</Label></div>
              </div>
              <div><Label>Description</Label><Input data-testid="camp-desc" value={form.description||""} onChange={(e)=>setForm({...form, description:e.target.value})} /></div>
              <div><Label>Dial mode</Label>
                <Select value={form.mode} onValueChange={(v)=>setForm({...form, mode:v})}>
                  <SelectTrigger data-testid="camp-mode" className="h-9"><SelectValue/></SelectTrigger>
                  <SelectContent>{MODES.map(m => <SelectItem key={m.v} value={m.v}>{m.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Agent channel mode</Label>
                <Select value={form.channel_mode_default || "auto"} onValueChange={(v)=>setForm({...form, channel_mode_default:v})}>
                  <SelectTrigger data-testid="camp-channel-mode" className="h-9"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto — follow dial mode (recommended)</SelectItem>
                    <SelectItem value="on_hook">On-hook — agent's browser rings for each call (lower CPU, ~1s latency)</SelectItem>
                    <SelectItem value="off_hook">Off-hook — persistent agent channel (zero-latency, predictive-ready)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground mt-1">
                  Auto: predictive/progressive/blended default to <strong>off-hook</strong>; preview/inbound default to <strong>on-hook</strong>. Override here to force a specific behavior.
                </p>
              </div>
              <div>
                <Label>Mapped lead lists</Label>
                <div className="min-h-9 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm" data-testid="camp-mapped-lists">
                  {editing && editing !== "new"
                    ? (mappedLists(editing.id).length
                        ? <div className="flex flex-wrap gap-1.5">{mappedLists(editing.id).map((l) => <Badge key={l.id} variant="secondary" className="text-[11px]">{l.name} <span className="opacity-60 ml-1">({l.data?.contacts_count ?? 0})</span></Badge>)}</div>
                        : <span className="text-amber-600 text-xs">No lists mapped — map from Admin → Outbound → Lead Lists</span>)
                    : <span className="text-muted-foreground text-xs">Save the campaign, then map lists to it from the Lead Lists page.</span>}
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">Lead lists are mapped to campaigns from the Lead Lists page. A campaign can have multiple lists.</p>
              </div>
              <div><Label>SIP trunk</Label><RelationalPicker kind="sip_trunks" value={form.trunk_id} onChange={(v)=>setForm({...form, trunk_id:v})} testId="camp-trunk" /></div>
              <div><Label>Caller-ID pool</Label><RelationalPicker kind="caller_id_pool" value={form.caller_id_pool_id} onChange={(v)=>setForm({...form, caller_id_pool_id:v})} testId="camp-cidpool" /><p className="text-[11px] text-muted-foreground mt-1">Optional. When set, each outbound call rotates through this pool's numbers using the pool's strategy. Leave empty to use the trunk's default caller-ID. Manage pools under <strong>Voice → Caller-ID Pool</strong>.</p></div>
              <div><Label>CRM screen (agent lead popup)</Label>
                <Select value={form.crm_screen_id || "__default__"} onValueChange={(v)=>setForm({...form, crm_screen_id: v === "__default__" ? "" : v})}>
                  <SelectTrigger data-testid="camp-crm-screen" className="h-9"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__default__">Use tenant default screen</SelectItem>
                    {crmScreens.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{s.is_default ? " (default)" : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground mt-1">Agents who join this campaign see this field layout in the customer lead popup. Manage screens under <strong>CRM Screens</strong>.</p>
              </div>
            </TabsContent>

            <TabsContent value="routing" className="space-y-3 mt-3">
              <div>
                <Label>Agent routing strategy</Label>
                <Select value={form.routing_strategy || "round_robin"} onValueChange={(v)=>setForm({...form, routing_strategy:v})}>
                  <SelectTrigger data-testid="camp-routing" className="h-9"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="round_robin">Round-robin (fair rotation — default)</SelectItem>
                    <SelectItem value="longest_idle">Longest idle (agent waiting longest dials first)</SelectItem>
                    <SelectItem value="fewest_calls_today">Fewest calls today (level daily KPIs)</SelectItem>
                    <SelectItem value="least_busy">Least busy (lowest current load)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground mt-1">
                  When multiple off-hook agents are signed-in to this campaign, the next answered call goes to whoever this strategy picks. Has no effect with a single agent.
                </p>
              </div>
              <div><Label>Required skills</Label><RelationalPicker kind="skills" multi returnKey="name" value={form.skills_required} onChange={(v)=>setForm({...form, skills_required:v})} testId="camp-skills" /></div>
              <div><Label>Music on hold</Label><RelationalPicker kind="music_on_hold" value={form.music_on_hold_id} onChange={(v)=>setForm({...form, music_on_hold_id:v})} testId="camp-moh" /></div>
              <div className="grid sm:grid-cols-3 gap-3">
                <div><Label>Max retry attempts</Label><Input data-testid="camp-retry-max" type="number" value={form.retry_max_attempts} onChange={(e)=>setForm({...form, retry_max_attempts:Number(e.target.value)})} /></div>
                <div><Label>Gap (minutes)</Label><Input data-testid="camp-retry-gap" type="number" value={form.retry_gap_minutes} onChange={(e)=>setForm({...form, retry_gap_minutes:Number(e.target.value)})} /></div>
                <div><Label>Recycle after (days)</Label><Input data-testid="camp-retry-recycle" type="number" value={form.retry_recycle_after_days} onChange={(e)=>setForm({...form, retry_recycle_after_days:Number(e.target.value)})} /></div>
              </div>
              {form.mode === "predictive" && (
                <div><Label>Abandon target %</Label><Input data-testid="camp-abandon" type="number" step="0.1" value={form.abandon_target_pct} onChange={(e)=>setForm({...form, abandon_target_pct:Number(e.target.value)})} /></div>
              )}
              {form.mode === "power" && (
                <div className="space-y-1.5 p-3 rounded-md border border-blue-300 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30">
                  <Label className="text-blue-900 dark:text-blue-200">Dial level (1:N ratio) <span className="text-red-500">*</span></Label>
                  <div className="flex items-center gap-3">
                    <Input
                      data-testid="camp-dial-level"
                      type="number"
                      step="0.1"
                      min="1.0"
                      max="4.0"
                      value={form.dial_level ?? 1.0}
                      onChange={(e) => {
                        // Clamp 1.0–4.0 and snap to 0.1 increments.
                        let v = Number(e.target.value);
                        if (!Number.isFinite(v)) v = 1.0;
                        v = Math.round(v * 10) / 10;
                        v = Math.max(1.0, Math.min(4.0, v));
                        setForm({ ...form, dial_level: v });
                      }}
                      className="w-24"
                    />
                    <span className="text-xs font-mono text-blue-800 dark:text-blue-300">
                      1 agent → {(form.dial_level ?? 1.0).toFixed(1)} lines · 10 agents → {Math.floor((form.dial_level ?? 1.0) * 10)} lines
                    </span>
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    Auto-dial ratio. Range <strong>1.0 – 4.0</strong> (0.1 steps). Lines dialed = <code>floor(ready agents × dial level)</code> minus calls already in flight. Higher = more dials per ready agent (risks higher abandon). No Erlang-C — the system trusts your ratio exactly.
                  </p>
                </div>
              )}
              {(form.mode === "predictive" || form.mode === "power") && (
                <div className="space-y-2 p-3 rounded-md border border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30">
                  <Label className="text-emerald-900 dark:text-emerald-200">Customer max wait (sec)</Label>
                  <div className="flex items-center gap-3">
                    <Input
                      data-testid="camp-customer-max-wait"
                      type="number"
                      min="0"
                      max="30"
                      step="1"
                      value={form.customer_max_wait_seconds ?? 3}
                      onChange={(e) => setForm({ ...form, customer_max_wait_seconds: Math.max(0, Math.min(30, Number(e.target.value) || 0)) })}
                      className="w-20"
                    />
                    <span className="text-[11px] text-muted-foreground">
                      Grace period an answered customer waits for a free agent before being dropped (abandon).
                    </span>
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    Calls are dialed unassigned and handed to the first free agent the instant the customer answers — maximises utilisation &amp; cuts empty-conf drops.
                  </p>
                </div>
              )}
              {/* Slice 16 — Call recording (MixMonitor) */}
              <div className="space-y-2 p-3 rounded-md border border-violet-300 dark:border-violet-800 bg-violet-50 dark:bg-violet-950/30">
                <div className="flex items-center gap-2">
                  <Switch
                    data-testid="camp-record-calls"
                    checked={!!form.record_calls}
                    onCheckedChange={(v) => setForm({ ...form, record_calls: !!v })}
                  />
                  <Label className="text-violet-900 dark:text-violet-200">Record calls</Label>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Records <strong>both inbound</strong> (via this campaign's linked queues) <strong>and outbound</strong> calls to the server via Asterisk MixMonitor. Recordings appear in <strong>Quality → Recordings</strong> with Play &amp; Download. Choose a <strong>Recording rule</strong> below to set format and consent.
                </p>
                {form.record_calls && (
                  <div className="pt-1">
                    <Label className="text-xs">Recording rule</Label>
                    <RelationalPicker
                      kind="recording_rules"
                      value={form.recording_rule_id}
                      onChange={(v) => setForm({ ...form, recording_rule_id: v })}
                      testId="camp-recording-rule"
                    />
                    <p className="text-[11px] text-muted-foreground mt-1">
                      Controls format, stereo &amp; the consent prompt played to the customer after they answer. Manage under <strong>Compliance → Recording Rules</strong>. If left empty, calls record as WAV with no consent prompt.
                    </p>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="schedule" className="space-y-3 mt-3">
              <CallingWindowEditor value={form.calling_window} onChange={(cw)=>setForm({...form, calling_window:cw})} testPrefix="camp-cw" />
              <p className="text-[11px] text-muted-foreground border-t border-border pt-2">
                Applies to predictive, progressive, power and preview dialing. The per-region regulatory
                compliance window still applies on top unless you enable
                <strong> Override regulatory compliance window</strong> on the Compliance tab.
              </p>
            </TabsContent>

            <TabsContent value="compliance" className="space-y-3 mt-3">
              <div><Label>Compliance rule</Label><RelationalPicker kind="compliance_rules" value={form.compliance_rule_id} onChange={(v)=>setForm({...form, compliance_rule_id:v})} testId="camp-comp" /></div>
              <div><Label>DNC lists</Label><RelationalPicker kind="dnc_lists" multi value={form.dnc_list_ids} onChange={(v)=>setForm({...form, dnc_list_ids:v})} testId="camp-dnc" /></div>
              <div className="flex items-center gap-2"><Switch data-testid="camp-amd" checked={!!form.amd_enabled} onCheckedChange={(v)=>setForm({...form, amd_enabled:!!v})} /><Label>AMD enabled (uses tenant-wide AMD settings)</Label></div>
              <div className="pt-2 border-t border-amber-200 dark:border-amber-500/20">
                <div className="flex items-center gap-2">
                  <Switch data-testid="camp-skip-compliance" checked={!!form.skip_compliance} onCheckedChange={(v)=>setForm({...form, skip_compliance:!!v})} />
                  <Label className="text-amber-700 dark:text-amber-300">Override regulatory compliance window</Label>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">
                  When ON, dialing hours are governed <strong>only</strong> by this campaign&apos;s <strong>Calling Window</strong> (Schedule tab);
                  the per-region regulatory default (e.g. 10:00&ndash;21:00) is ignored. Leave OFF to keep the regulatory window applied on top.
                  Set a Calling Window before enabling &mdash; otherwise dialing is unrestricted.
                </p>
              </div>
            </TabsContent>

            <TabsContent value="agent" className="space-y-3 mt-3">
              <div><Label>Allowed dispositions</Label><RelationalPicker kind="dispositions" multi value={form.allowed_dispositions} onChange={(v)=>setForm({...form, allowed_dispositions:v})} testId="camp-disp" /></div>
              <div><Label>Allowed pause codes</Label><RelationalPicker kind="pause-codes" multi value={form.allowed_pause_codes} onChange={(v)=>setForm({...form, allowed_pause_codes:v})} testId="camp-pause" /></div>
              <div className="border-t pt-3">
                <div className="flex items-center gap-2 mb-2"><Switch data-testid="camp-autowrap" checked={!!form.auto_wrapup_enabled} onCheckedChange={(v)=>setForm({...form, auto_wrapup_enabled:!!v})} /><Label>Auto wrap-up if agent doesn't pick a disposition</Label></div>
                {form.auto_wrapup_enabled && (
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div><Label>Auto wrap-up seconds</Label><Input data-testid="camp-autowrap-sec" type="number" value={form.auto_wrapup_seconds} onChange={(e)=>setForm({...form, auto_wrapup_seconds:Number(e.target.value)})} /></div>
                    <div><Label>Fallback wrap-up code</Label><RelationalPicker kind="wrapup-codes" value={form.auto_wrapup_code_id} onChange={(v)=>setForm({...form, auto_wrapup_code_id:v})} testId="camp-autowrap-code" /></div>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="blended" className="space-y-3 mt-3">
              <p className="text-xs text-muted-foreground">Link this campaign to one or more inbound queues. Agents who join this campaign are automatically added to all linked queues (they can opt out of individual queues from the workspace topbar).</p>
              <div>
                <Label>Linked inbound queues</Label>
                <QueueMultiPicker
                  selected={form.linked_queues || (form.linked_queue_id ? [form.linked_queue_id] : [])}
                  onChange={(arr) => setForm({ ...form, linked_queues: arr, linked_queue_id: arr[0] || "" })}
                />
              </div>
              <div><Label>Blended priority</Label>
                <Select value={form.blended_priority} onValueChange={(v)=>setForm({...form, blended_priority:v})}>
                  <SelectTrigger data-testid="camp-blendprio" className="h-9"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inbound_first">Inbound first (recommended)</SelectItem>
                    <SelectItem value="outbound_first">Outbound first</SelectItem>
                    <SelectItem value="inbound_only_when_queue_above">Inbound only when queue depth above threshold</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter><Button variant="outline" onClick={()=>setEditing(null)}>Cancel</Button><Button data-testid="camp-save" onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90 text-primary-foreground">{saving?<Loader2 className="w-4 h-4 animate-spin"/>:"Save"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---- multi-queue chip selector (used in the Blended tab) ------------------
function QueueMultiPicker({ selected = [], onChange }) {
  const [queues, setQueues] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/admin/resources/queues")
      .then((r) => setQueues(r.data || []))
      .catch(() => setQueues([]))
      .finally(() => setLoading(false));
  }, []);
  function toggle(id) {
    const next = selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id];
    onChange(next);
  }
  if (loading) return <div className="text-xs text-muted-foreground italic">Loading queues…</div>;
  if (queues.length === 0) return <div className="text-xs text-muted-foreground italic">No queues defined yet — create one under Voice → Queues first.</div>;
  return (
    <div className="flex flex-wrap gap-1.5 mt-1.5 max-h-44 overflow-y-auto">
      {queues.map((q) => {
        const on = selected.includes(q.id);
        return (
          <button
            key={q.id}
            type="button"
            data-testid={`camp-queue-${q.id}`}
            onClick={() => toggle(q.id)}
            className={`px-2.5 py-1 rounded-full text-xs border transition ${
              on
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-card border-border hover:bg-muted"
            }`}
          >
            {q.name || q.label || q.id}
          </button>
        );
      })}
    </div>
  );
}

